import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { type ContentFrameworkColumn, type ContentFrameworkPrinciple } from "@/lib/composerCore";

export type PrincipleSlotProps = {
  workspaceId: string;
  column: ContentFrameworkColumn;
  position: number;
  principle: ContentFrameworkPrinciple | null;
  showElaborations: boolean;
  onChange: () => void | Promise<void>;
};

export function PrincipleSlot({ workspaceId, column, position, principle, showElaborations, onChange }: PrincipleSlotProps) {
  const [label, setLabel] = useState(principle?.label ?? "");
  const [description, setDescription] = useState(principle?.description ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLabel(principle?.label ?? "");
    setDescription(principle?.description ?? "");
  }, [principle?.id, principle?.label, principle?.description]);

  const dirty =
    label.trim() !== (principle?.label ?? "") ||
    description.trim() !== (principle?.description ?? "");

  const save = async () => {
    const trimmed = label.trim();
    const trimmedDesc = description.trim();
    if (!trimmed) {
      if (principle) {
        const { error } = await supabase.from("content_framework_principles").delete().eq("id", principle.id);
        if (error) {
          toast.error(error.message);
          return;
        }
        await onChange();
      }
      return;
    }
    setSaving(true);
    if (principle) {
      const { error } = await supabase
        .from("content_framework_principles")
        .update({ label: trimmed, description: trimmedDesc || null })
        .eq("id", principle.id);
      setSaving(false);
      if (error) return toast.error(error.message);
    } else {
      const { error } = await supabase.from("content_framework_principles").insert({
        workspace_id: workspaceId,
        framework_column_id: column.id,
        label: trimmed,
        description: trimmedDesc || null,
        position,
      });
      setSaving(false);
      if (error) return toast.error(error.message);
    }
    await onChange();
  };

  const hasElab = (principle?.description ?? "").trim().length > 0;

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          onBlur={() => { if (dirty && !showElaborations) void save(); }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              (event.target as HTMLInputElement).blur();
            }
          }}
          placeholder={`Principle ${position + 1}`}
          disabled={saving}
        />
        {!showElaborations && hasElab ? (
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/60"
            aria-label="Has elaboration"
            title="Has elaboration"
          />
        ) : null}
      </div>
      {showElaborations ? (
        <Textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          onBlur={() => { if (dirty) void save(); }}
          placeholder="Elaboration — what this principle means for the planning/writing agents"
          rows={2}
          disabled={saving || !label.trim()}
        />
      ) : null}
    </div>
  );
}
